import { gql } from '@apollo/client';
import { CORE_TOOL_CATALOG_FIELDS } from './tool-catalog';
import { CORE_TOOL_CATEGORY_FIELDS } from './tool-category';
import { CORE_TOOL_ORDER_CATALOG_FIELDS } from './tool-order-catalog';
import { CORE_TOOL_TYPE_FIELDS } from './tool-type';
import { CORE_USER_FIELDS } from './user';

const CORE_TOOL_ORDER_FIELDS = gql`
  ${CORE_TOOL_TYPE_FIELDS}
  ${CORE_TOOL_CATEGORY_FIELDS}
  ${CORE_TOOL_CATALOG_FIELDS}
  ${CORE_TOOL_ORDER_CATALOG_FIELDS}
  fragment ToolOrderCoreFields on ToolOrder {
    id
    orderNumber
    requiredStartDate
    status
    crewCount
    stats {
      id
      consumablesCount
      toolsCount
    }
    orderDate
    recipientId
    recipientEmployee {
      id
      firstName
      lastName
      middleName
      email
      lobEmployeeId
      userId
      projectId
      user {
        id
        bun
      }
      imageUrl
    }
    orderPlacedFor
    orderPlacedForUser {
      #deprecated use recipientEmployee instead
      id
      bun
      firstName
      lastName
      employee {
        id
        firstName
        lastName
        middleName
        email
        lobEmployeeId
        userId
        projectId
        user {
          id
          bun
        }
        imageUrl
      }
    }
    projectId
    toolRoomId
    toolRoom {
      id
      barCode
      description
    }
    toolOrderCatalogs {
      ...CoreToolOrderCatalogFields
      toolCatalog {
        ...CoreToolCatalogFields
        toolCategory {
          ...CoreToolCategoryFields
          toolType {
            ...CoreToolTypeFields
          }
        }
      }
      similarToolCatalog {
        ...CoreToolCatalogFields
        toolCategory {
          ...CoreToolCategoryFields
          toolType {
            ...CoreToolTypeFields
          }
        }
      }
      uom {
        id
        abbreviation
        description
      }
      toolOrderInventories {
        id
        # checkedIn fields are no longer part of tool order inventory
        dispatchQuantity
        toolInventoryId
        toolOrderCatalogId
        dispatcherComments
        dispatcherCommentsBy
        status
        toolInventory {
          id
          barCode
          toolCatalogId
          description
          isSerialized
          onHandCount
          toolCatalog {
            id
            toolCatalogImageUrl
            isLocked
            toolCategory {
              id
              name
            }
          }
        }
      }
    }
  }
`;
export const CREATE_TOOL_ORDER = gql`
  ${CORE_TOOL_ORDER_FIELDS}
  mutation CreateToolOrder($createToolOrderInput: CreateToolOrderInput!) {
    createToolOrder(createToolOrderInput: $createToolOrderInput) {
      toolOrder {
        ...ToolOrderCoreFields
      }
      error
    }
  }
`;

export const UPDATE_TOOL_ORDER = gql`
  ${CORE_TOOL_ORDER_FIELDS}
  mutation updateToolOrder($updateToolOrderInput: UpdateToolOrderInput!) {
    updateToolOrder(input: $updateToolOrderInput) {
      toolOrder {
        ...ToolOrderCoreFields
      }
      error
    }
  }
`;

export const MARK_PICK_UP_READY = gql`
  ${CORE_TOOL_ORDER_FIELDS}
  mutation markPickUpReady($id: ID!) {
    markPickUpReady(id: $id) {
      toolOrder {
        ...ToolOrderCoreFields
      }
    }
  }
`;

export const UPDATE_TOOL_ORDER_STATUS = gql`
  mutation UpdateToolOrderStatus($id: ID!, $status: ToolOrderStatus) {
    updateToolOrderStatus(id: $id, status: $status) {
      toolOrder {
        id
        orderNumber
        status
      }
      error
    }
  }
`;

export const ADVANCE_TOOL_ORDER_STATUS = gql`
  ${CORE_TOOL_ORDER_FIELDS}
  mutation AdvanceToolOrderStatus($advanceToolOrderStatusInput: AdvanceToolOrderStatusInput!) {
    advanceToolOrderStatus(advanceToolOrderStatusInput: $advanceToolOrderStatusInput) {
      toolOrder {
        ...ToolOrderCoreFields
      }
      error
    }
  }
`;
export const DISPATCH_TOOL_ORDER = gql`
  ${CORE_TOOL_ORDER_FIELDS}
  mutation DispatchToolOrder($dispatchToolOrderInput: DispatchToolOrderInput!) {
    dispatchToolOrder(dispatchToolOrderInput: $dispatchToolOrderInput) {
      toolOrder {
        ...ToolOrderCoreFields
      }
      error
    }
  }
`;

export const SEARCH_TOOL_ORDER = gql`
  ${CORE_TOOL_ORDER_FIELDS}
  query SearchToolOrders($searchInput: SearchInput!) {
    searchToolOrders(searchInput: $searchInput) {
      items {
        ...ToolOrderCoreFields
        toolOrderCatalogs {
          id
          quantity
          requesterComments
          toolOrderId
          toolCatalogId
          nonCatalogDescription
          nonCatalogReferenceUrl
          nonCatalogSimilarToId
          nonCatalogUomId
        }
        stats {
          id
          consumablesCount
          toolsCount
        }
      }
    }
  }
`;

export const GET_TOOL_ORDER_BY_ID = gql`
  ${CORE_TOOL_ORDER_FIELDS}
  ${CORE_USER_FIELDS}
  query ToolOrderById($toolOrderByIdId: ID!) {
    toolOrderById(id: $toolOrderByIdId) {
      ...ToolOrderCoreFields
      createdBy
      checkedOutByUser {
        bun
        firstName
        lastName
      }
      # these are the stats of the parent order
      stats {
        id
        consumablesCount
        toolsCount
      }
      outOfStockToolOrder {
        orderNumber
        createdBy
        createdDate
        createdByUser {
          ...CoreUserFields
          roles {
            id
            name
            description
          }
        }
        stats {
          id
          consumablesCount
          toolsCount
        }
        toolRoom {
          id
          name
        }
      }
    }
  }
`;

export const GET_TOOL_ORDER_WITH_AVAILABLE_INVENTORY = gql`
  ${CORE_TOOL_ORDER_FIELDS}
  ${CORE_USER_FIELDS}
  query ToolOrderById($toolOrderByIdId: ID!) {
    toolOrderById(id: $toolOrderByIdId) {
      ...ToolOrderCoreFields
      thTransactionNo
      createdByUser {
        ...CoreUserFields
      }
      checkedOutByUser {
        ...CoreUserFields
      }
      availableToolInventories {
        toolOrderId
        toolCatalogId
        quantityOnHand
      }
      outOfStockToolOrder {
        orderNumber
        createdBy
        createdDate
        createdByUser {
          ...CoreUserFields
          roles {
            id
            name
          }
        }
        stats {
          id
          consumablesCount
          toolsCount
        }
        toolRoom {
          id
          name
        }
      }
      recipientEmployee {
        id
        crewId
        crew {
          id
          crewCount
        }
      }
    }
  }
`;
export const GET_TOOL_ORDER_STATS = gql`
  query ToolOrderStats($toolOrderStatsInput: ToolOrderStatsInput!) {
    toolOrderStats(toolOrderStatsInput: $toolOrderStatsInput) {
      result {
        completedCount
        openCount
        outOfStockCount
        cancelledCount
        preparedCount
      }
      error
    }
  }
`;
export const CREATE_ADHOC_TOOL_ORDER = gql`
  ${CORE_TOOL_ORDER_FIELDS}
  mutation CreateAdhocToolOrder($createAdhocOrderInput: CreateAdhocOrderInput!) {
    createAdhocToolOrder(createAdhocOrderInput: $createAdhocOrderInput) {
      toolOrder {
        ...ToolOrderCoreFields
      }
      error
    }
  }
`;

export const TOOL_ORDER_COUNT_FOR_SEARCH = gql`
  query ToolOrdersCountForSearch($searchInput: SearchInput) {
    toolOrdersCountForSearch(searchInput: $searchInput)
  }
`;
